import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import SVG from './svg';

const Fullscreen = styled.div`
  position: fixed;
  z-index: 100;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.25);
  display: flex;
  justify-content: center;
  align-items: center;
`;

const ModalBlock = styled.div`
  width: 620px;
  max-width: calc(100vw - 32px);
  background: white;
  border-radius: 10px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.18);
  overflow: hidden;
`;

const TitleBlock = styled.div`
  .titleBox {
    width: 100%;
    height: 56px;
    background: #eeeff0;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0 20px;
    box-sizing: border-box;
  }
    

  .titleText {
    font-family: 'Source Sans Pro';
    font-size: 20px;
    font-weight: 600;
    color: #59616a;
  }

  .closeIcon {
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
  }
`;

const ContentBlock = styled.div`
  padding: 18px 24px 10px;
  color: #59616a;
  font-family: 'Source Sans Pro';
`;

const Subtitle = styled.div`
  margin-top: 6px;
  font-size: 14px;
  color: #7a838d;
`;

const SubtitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const HelpIconButton = styled.button`
  appearance: none;
  width: 22px;
  height: 22px;
  border-radius: 999px;
  border: 1px solid #cfd5db;
  background: #fff;
  color: #59616a;

  display: inline-flex;
  align-items: center;
  justify-content: center;

  font-family: 'Source Sans Pro';
  font-size: 13px;
  font-weight: 700;
  line-height: 1;

  cursor: pointer;

  &:hover {
    border-color: ${(props) => props.theme?.main_color || '#ff6d22'};
    color: ${(props) => props.theme?.main_color || '#ff6d22'};
  }

  &:focus-visible {
    outline: 3px solid ${(props) => props.theme?.main_color || '#ff6d22'};
    outline-offset: 2px;
  }
`;

const HelpCtaButton = styled.button`
  appearance: none;
  border: 1px solid #cfd5db;
  background: #fff;
  color: ${(props) => props.theme?.main_color || '#ff6d22'};
  border-radius: 999px;
  padding: 6px 10px;

  font-family: 'Source Sans Pro';
  font-size: 13px;
  font-weight: 700;
  line-height: 1;

  cursor: pointer;
  margin-left: auto;

  &:hover {
    border-color: ${(props) => props.theme?.main_color || '#ff6d22'};
    background: #fff8f5;
  }

  &:focus-visible {
    outline: 3px solid ${(props) => props.theme?.main_color || '#ff6d22'};
    outline-offset: 2px;
  }
`;

const HelpOverlay = styled.div`
  position: fixed;
  z-index: 110; /* au-dessus de Fullscreen (100) */
  inset: 0;
  background: rgba(0, 0, 0, 0.25);
  display: flex;
  justify-content: center;
  align-items: center;
`;

const HelpModal = styled.div`
  width: 520px;
  max-width: calc(100vw - 32px);
  background: #fff;
  border-radius: 10px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.18);
  overflow: hidden;
`;

const HelpHeader = styled.div`
  height: 52px;
  background: #eeeff0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 16px;
  box-sizing: border-box;

  .title {
    font-family: 'Source Sans Pro';
    font-size: 16px;
    font-weight: 600;
    color: #59616a;
  }

  .close {
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
  }
`;

const HelpBody = styled.div`
  padding: 16px;
  color: #59616a;
  font-family: 'Source Sans Pro';
  font-size: 14px;
  line-height: 1.55;

  ol {
    margin: 10px 0 0 18px;
    padding: 0;
  }

  li + li {
    margin-top: 8px;
  }

  .hint {
    margin-top: 10px;
    color: #7a838d;
    font-size: 13px;
  }

  code {
    font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono',
      'Courier New', monospace;
    font-size: 12px;
  }

  .help-image {
    margin-top: 16px;
    width: 100%;
    border-radius: 6px;
    border: 1px solid #e6e8ea;
  }
`;

const GPUSelectionBlock = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 16px;
  flex-wrap: wrap;
`;

const GPUButton = styled.button`
  appearance: none;
  padding: 10px 20px;
  border-radius: 8px;
  font-family: 'Source Sans Pro';
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 150ms ease;

  border: 2px solid ${(props) => (props.selected ? (props.theme?.main_color || '#ff6d22') : '#e6e8ea')};
  background: ${(props) => (props.selected ? '#fff8f5' : '#fff')};
  color: ${(props) => (props.selected ? (props.theme?.main_color || '#ff6d22') : '#59616a')};

  &:hover {
    border-color: ${(props) => props.theme?.main_color || '#ff6d22'};
    background: #fff8f5;
  }

  &:focus-visible {
    outline: 3px solid ${(props) => props.theme?.main_color || '#ff6d22'};
    outline-offset: 2px;
  }
`;

const ButtonBlock = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 18px;
  padding: 18px 24px 24px;
  border-top: 1px solid #eeeff0;

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const ChoiceCard = styled.button`
  appearance: none;
  border: 1px solid #e6e8ea;
  background: #fff;
  border-radius: 12px;
  padding: 14px;
  cursor: pointer;
  text-align: left;

  display: grid;
  grid-template-rows: auto auto;
  gap: 12px;

  transition: transform 120ms ease, box-shadow 120ms ease, border-color 120ms ease;

  &:hover {
    transform: translateY(-2px);
    border-color: ${(props) => props.theme?.main_color || '#ff6d22'};
    box-shadow: 0 8px 18px rgba(0, 0, 0, 0.12);
  }

  &:active {
    transform: translateY(0px);
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.55;
    transform: none;
    box-shadow: none;
  }

  &:focus-visible {
    outline: 3px solid ${(props) => props.theme?.main_color || '#ff6d22'};
    outline-offset: 2px;
  }
`;

const CardImage = styled.img`
  width: 100%;
  height: 220px;
  object-fit: contain;
  display: block;

  @media (max-width: 640px) {
    height: 200px;
  }
`;

const CardTitle = styled.div`
  font-family: 'Source Sans Pro';
  font-size: 16px;
  font-weight: 600;
  color: #59616a;
`;

const CardHint = styled.div`
  font-family: 'Source Sans Pro';
  font-size: 13px;
  color: #7a838d;
  margin-top: 2px;
`;

const SW_VERSION_URL = 'https://updatewithjson.s3.eu-central-1.amazonaws.com/sw_version.json';

const GPUDetectionModal = ({ show, onClose }) => {
  const { t } = useTranslation();
  const { odm } = useSelector(({ app }) => ({ odm: app.odm }));
  
  const [loading, setLoading] = useState(false);

  const getJsonKey = () => {
    if (odm?.name === 'EZSCAN') return 'Vatech';
    if (odm?.name === 'IMESICORE') return 'imes-icore';
    return '3Disc';
  };

  const fetchAndDownload = async (productKey) => {
    setLoading(true);
    try {
      const response = await fetch(SW_VERSION_URL);
      const data = await response.json();
      
      console.log('JSON data:', data);
      
      let downloadUrl = null;
      const jsonKey = getJsonKey();
      
      if (data[jsonKey]?.[productKey]?.Link) {
        downloadUrl = data[jsonKey][productKey].Link;
      }
      else if (data[productKey]?.Link) {
        downloadUrl = data[productKey].Link;
      }
      else if (data[productKey]?.download_url) {
        downloadUrl = data[productKey].download_url;
      }
      else if (data[jsonKey]?.[productKey]?.download_url) {
        downloadUrl = data[jsonKey][productKey].download_url;
      }
      
      console.log(`Download URL for ${jsonKey}/${productKey}:`, downloadUrl);
      
      if (downloadUrl) {
        window.open(downloadUrl, '_blank');
        onClose();
      } else {
        console.error(`No download URL found for ${jsonKey}/${productKey}. JSON structure:`, JSON.stringify(data, null, 2));
        alert(t('Download link not available. Please try again later.'));
      }
    } catch (error) {
      console.error('Failed to fetch download link:', error);
      alert(t('Failed to fetch download link. Please try again later.'));
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadOVO = () => {
    fetchAndDownload('OVO');
  };

  const handleDownloadOVO3 = () => {
    fetchAndDownload('OVO3');
  };

  useEffect(() => {
    if (!show) return;

    const onKeyDown = (e) => {
      if (e.key === 'Escape') {
        onClose?.();
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [show, onClose]);

  if (!show) return null;

  const isEzscan = odm?.name === 'EZSCAN';
  const ovoImg = isEzscan
    ? `${process.env.PUBLIC_URL}/images/odm/EZSCAN/download/ovo-540.png`
    : `${process.env.PUBLIC_URL}/images/download/ovo-540.png`;
  const ovo3Img = isEzscan
    ? `${process.env.PUBLIC_URL}/images/odm/EZSCAN/download/ovo3-540.png`
    : `${process.env.PUBLIC_URL}/images/download/ovo3-540.png`;

  return (
    <Fullscreen onClick={() => onClose?.()}>
      <ModalBlock
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label={t('Download Software')}
      >
        <TitleBlock>
          <div className="titleBox">
            <div className="titleText">{t('Choose your software to download:')}</div>
            <div className="closeIcon" onClick={onClose}>
              <SVG
                name="close"
                color="#878D93"
                colorOver={odm?.main_color_hover || '#ff6d22'}
                colorOut="#878D93"
                colorDown={odm?.main_color || '#ff6d22'}
              />
            </div>
          </div>
        </TitleBlock>

        <ButtonBlock>
          <ChoiceCard 
            type="button" 
            onClick={handleDownloadOVO} 
            aria-label={t('Download OVO')}
            disabled={loading}
          >
            <CardImage
              src={ovoImg}
              alt="OVO"
              onError={(e) => {
                e.currentTarget.onerror = null;
                e.currentTarget.src = '/images/download/ovo-540.png';
              }}
            />
            <div>
              {!isEzscan && <CardTitle>OVO</CardTitle>}
              <CardHint>{loading ? t('Loading...') : t('Download for Windows')}</CardHint>
            </div>
          </ChoiceCard>

          <ChoiceCard 
            type="button" 
            onClick={handleDownloadOVO3} 
            aria-label={t('Download OVO 3')}
            disabled={loading}
          >
            <CardImage
              src={ovo3Img}
              alt="OVO 3"
              onError={(e) => {
                e.currentTarget.onerror = null;
                e.currentTarget.src = '/images/download/ovo3-540.png';
              }}
            />
            <div>
              {!isEzscan && <CardTitle>OVO 3</CardTitle>}
              <CardHint>{loading ? t('Loading...') : t('Download for Windows')}</CardHint>
            </div>
          </ChoiceCard>
        </ButtonBlock>
      </ModalBlock>
    </Fullscreen>
  );
};

export default GPUDetectionModal;